import { useCallback, useEffect, useState } from 'react';
import { getCmsApiUrl, getBackendUrl } from '../../../services/configService';
import { proxyCmsUrl } from '../../../services/cmsFileProxy';
import type { VAREntity } from '../../../types/var';

export interface VARPhoto {
  id: string;          // CMS node UUID
  nid: string;         // CMS drupal_internal__nid (used to match PG metadata + delete)
  imageFileUuid: string | null; // CMS file entity UUID (for orphan file cleanup on delete)
  title: string;
  description: string;
  thumbnailUrl: string | null;
  fullUrl: string | null;
  cardinal: string | null;
  disrepair: boolean | null;
  forestFuels: boolean | null;
  fuelMitigation: boolean | null;
  fuelProximity: boolean | null;
  hazmat: boolean | null;
  // PG photos array index — used by DELETE /api/var/:id/photos/:index
  pgIndex: number | null;
}

interface PgPhoto {
  FileUrl?: string;
  Description?: string | null;
  Cardinal?: string | null;
  Disrepair?: boolean | string | null;
  ForestFuels?: boolean | string | null;
  FuelMitigation?: boolean | string | null;
  FuelProximity?: boolean | string | null;
  Hazmat?: boolean | string | null;
}

export function coerceBool(v: unknown): boolean | null {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return null;
}


interface PhotoLoaderResult {
  entity: VAREntity | null;
  photos: VARPhoto[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Loads a VAR's photos by merging PG metadata (var_points.photos JSONB) with
 * CMS image data (JSON:API var_photo nodes). Match key: drupal_internal__nid
 * extracted from PG FileUrl ("/node/{nid}").
 */
export function usePhotoLoader(varId: string | null, enabled = true): PhotoLoaderResult {
  const [entity, setEntity] = useState<VAREntity | null>(null);
  const [photos, setPhotos] = useState<VARPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!varId || !enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const API_URL = getCmsApiUrl();
    const BACKEND_URL = getBackendUrl() || '';

    Promise.all([
      fetch(`${BACKEND_URL}/api/var/by-var-id/${encodeURIComponent(varId)}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }),
      fetch(
        `${API_URL}/jsonapi/node/var_photo?${new URLSearchParams({
          'filter[field_var_id]': varId,
          'include': 'field_image',
          'sort': '-created',
          'page[limit]': '50',
        })}`,
        { headers: { 'Accept': 'application/vnd.api+json' }, credentials: 'include' },
      ),
    ])
      .then(async ([pgRes, cmsRes]) => {
        if (cancelled) return;

        let pgEntity: VAREntity | null = null;
        let pgPhotos: PgPhoto[] = [];
        if (pgRes.ok) {
          const pgData = await pgRes.json();
          pgEntity = pgData.data || null;
          pgPhotos = (pgEntity?.photos as PgPhoto[]) || [];
        }
        setEntity(pgEntity);

        // Build nid -> { meta, pgIndex } map
        const metaByNid = new Map<string, { meta: PgPhoto; pgIndex: number }>();
        pgPhotos.forEach((p, idx) => {
          const m = p.FileUrl?.match(/\/node\/(\d+)/);
          if (m) metaByNid.set(m[1], { meta: p, pgIndex: idx });
        });

        if (!cmsRes.ok) {
          setPhotos([]);
          return;
        }
        const cmsData = await cmsRes.json();
        const included = cmsData.included || [];
        const nodes = cmsData.data || [];

        const mapped = nodes.map((node: any): VARPhoto => {
          const fileRef = node.relationships?.field_image?.data;
          const file = fileRef ? included.find((i: any) => i.id === fileRef.id) : null;
          const nid = String(node.attributes?.drupal_internal__nid || '');
          const matched = metaByNid.get(nid);
          const meta = matched?.meta || {};
          const rawThumb = file?.attributes?.image_style_uri?.thumbnail || null;
          const rawFull = file?.attributes?.image_style_uri?.large
            || file?.attributes?.uri?.url
            || null;
          return {
            id: node.id,
            nid,
            imageFileUuid: fileRef?.id ?? null,
            title: node.attributes?.title || '',
            description: meta.Description || '',
            thumbnailUrl: proxyCmsUrl(rawThumb),
            fullUrl: proxyCmsUrl(rawFull),
            cardinal: meta.Cardinal ?? null,
            disrepair: coerceBool(meta.Disrepair),
            forestFuels: coerceBool(meta.ForestFuels),
            fuelMitigation: coerceBool(meta.FuelMitigation),
            fuelProximity: coerceBool(meta.FuelProximity),
            hazmat: coerceBool(meta.Hazmat),
            pgIndex: matched?.pgIndex ?? null,
          };
        });
        setPhotos(mapped);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load photos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [varId, enabled, tick]);

  return { entity, photos, loading, error, refresh };
}
